import "./App.scss";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/layout/Navbar/Navbar";
import Sidebar from "./components/layout/Sidebar/Sidebar";
import { useState, useEffect, useCallback } from "react";
import { initializeApi, getApiInstance } from "./utils/api";
import Login from "./pages/login/Login";

import Components from "./pages/components/Components";
import Dashboard from "./pages/dashboard/Dashboard";
import Comunidades from "./pages/administracion/comunidades/Comunidades";
import Intermediarios from "./pages/administracion/intermediarios/Intermediarios";
import Paises from "./pages/administracion/paises/Paises";
import Productores from "./pages/administracion/productores/Productores";
import Provincias from "./pages/administracion/provincias/Provincias";
import Usuarios from "./pages/administracion/usuarios/Usuarios";
import CentrosProductores from "./pages/centrosProductores/CentrosProductores";
import Gestores from "./pages/gestores/Gestores";
import Residuos from "./pages/residuos/Residuos";
import CantidadesGeneradas from "./pages/informes/cantidadesGeneradas/CantidadesGeneradas";
import Costes from "./pages/informes/costes/Costes";
import HuellaCarbono from "./pages/informes/huellaCarbono/HuellaCarbono";
import SegregacionValorizacion from "./pages/informes/segregacionValorizacion/SegregacionValorizacion";
import ObrasMenores from "./pages/obrasMenores/ObrasMenores";
import { ZoneProvider } from "./context/ZoneContext";
import FichaGestor from "./pages/fichaGestor/FichaGestor";
import FichaCentroProductor from "./pages/fichaCentroProductor/FichaCentroProductor";
import FichaObraMenor from "./pages/fichaObraMenor/FichaObraMenor";
import PerfilUsuario from "./pages/perfilUsuario/perfilUsuario";
import FichaProductor from "./pages/administracion/fichaProductor/FichaProductor";

function App() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [user, setUser] = useState(null);

  const logoutUser = useCallback(() => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setUserRole(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const initialize = async () => {
      await initializeApi(logoutUser);
      const token = localStorage.getItem("token");

      if (token) {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));

        // Verificar si el token ha expirado
        const currentTime = Math.floor(Date.now() / 1000);
        if (decodedToken.exp && decodedToken.exp < currentTime) {
          logoutUser();
          return;
        }

        setIsAuthenticated(true);
        setUserRole(
          decodedToken[
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
          ]
        );

        // Extraer información del usuario del token
        setUser({
          name:
            decodedToken[
              "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
            ] || "Usuario",
          email:
            decodedToken[
              "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
            ] || "usuario@empresa.com",
          role:
            decodedToken[
              "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
            ] || "Usuario",
        });
      }
      setIsInitializing(false);
    };
    initialize();
  }, [logoutUser]);

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleToggleCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const authenticateUser = (token) => {
    localStorage.setItem("token", token);
    const decodedToken = JSON.parse(atob(token.split(".")[1]));
    setIsAuthenticated(true);
    setUserRole(
      decodedToken[
        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
      ]
    );

    // Extraer información del usuario del token
    setUser({
      name:
        decodedToken[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
        ] || "Usuario",
      email:
        decodedToken[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
        ] || "usuario@empresa.com",
      role:
        decodedToken[
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
        ] || "Usuario",
    });
  };

  if (isInitializing) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Cargando aplicación...
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        {!isAuthenticated ? (
          <Routes>
            <Route path="/*" element={<Navigate to="/login" />} />
            <Route
              path="/login"
              element={<Login onLogin={authenticateUser} />}
            />
          </Routes>
        ) : (
          <ZoneProvider>
            <Navbar
              onToggleSidebar={handleToggleSidebar}
              isSidebarOpen={isSidebarOpen}
              isSidebarCollapsed={isSidebarCollapsed}
              user={user}
              onLogout={logoutUser}
            />

            <div className="app__container">
              <Sidebar
                isOpen={isSidebarOpen}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={handleToggleCollapse}
                onClose={() => setIsSidebarOpen(false)}
                userRole={userRole}
              />

              <main
                className={`app__content ${
                  isSidebarCollapsed ? "app__content--collapsed" : ""
                }`}
              >
                <div className="app__content-wrapper">
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to="/dashboard" replace />}
                    />

                    <Route path="/login" element={<Navigate to="/" />} />

                    {/* Dashboard */}
                    <Route path="/dashboard" element={<Dashboard />} />

                    {/* Perfil de Usuario */}
                    <Route
                      path="/perfil-usuario"
                      element={<PerfilUsuario user={user} />}
                    />

                    {/* Administración */}
                    <Route
                      path="/administracion/comunidades"
                      element={<Comunidades />}
                    />
                    <Route
                      path="/administracion/intermediarios"
                      element={<Intermediarios />}
                    />
                    <Route path="/administracion/paises" element={<Paises />} />
                    <Route
                      path="/administracion/productores"
                      element={<Productores />}
                    />
                    <Route
                      path="/administracion/productores/:id"
                      element={<FichaProductor />}
                    />
                    <Route
                      path="/administracion/provincias"
                      element={<Provincias />}
                    />
                    <Route
                      path="/administracion/usuarios"
                      element={<Usuarios />}
                    />

                    {/* Otrass */}
                    <Route
                      path="/centros-productores"
                      element={<CentrosProductores />}
                    />
                    <Route path="/gestores" element={<Gestores />} />
                    <Route path="/gestores/:id" element={<FichaGestor />} />
                    <Route
                      path="/centros-productores/:id"
                      element={<FichaCentroProductor />}
                    />
                    <Route path="/obras-menores" element={<ObrasMenores />} />
                    <Route
                      path="/obras-menores/:id"
                      element={<FichaObraMenor />}
                    />
                    <Route path="/residuos" element={<Residuos />} />

                    {/*  Informes */}
                    <Route
                      path="/informes/cantidades-generadas"
                      element={<CantidadesGeneradas />}
                    />
                    <Route path="/informes/costes" element={<Costes />} />
                    <Route
                      path="/informes/huella-carbono"
                      element={<HuellaCarbono />}
                    />
                    <Route
                      path="/informes/segregacion-valorizacion"
                      element={<SegregacionValorizacion />}
                    />

                    {/* tests */}
                    <Route path="/components" element={<Components />} />
                    <Route
                      path="*"
                      element={<Navigate to="/dashboard" replace />}
                    />
                  </Routes>
                </div>
              </main>
            </div>
          </ZoneProvider>
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
